import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { Flag, CalendarDays, User } from 'lucide-react';
import { TaskFieldChip } from './TaskFieldChip';
import { TaskFieldEditor } from './TaskFieldEditor';
import type { FormFieldKey, TaskFieldOptions } from './TaskFieldEditor';
import type { QuickTaskDraftFormValues } from '../schemas';
import { taskFieldChipValue } from '../utils/taskFieldChipValue';

const TEST_ID_PREFIX = 'blank-task-field';

/** The three fields the blank form offers beyond title and description, in design order. */
const CHIPS: { field: FormFieldKey; icon: React.ReactNode }[] = [
  { field: 'priority', icon: <Flag /> },
  { field: 'dueDate', icon: <CalendarDays /> },
  { field: 'assigneeId', icon: <User /> },
];

interface BlankTaskChipsProps {
  control: Control<QuickTaskDraftFormValues>;
  /** Only the fields these chips render — the panel subscribes to those and no more. */
  values: Partial<QuickTaskDraftFormValues>;
  options: TaskFieldOptions;
  projectId: string;
  boardId: string;
}

/**
 * Priority, due date and assignee as the same pills the AI tab uses, in their own bordered
 * block. Same chip and same editors on purpose: the two tabs create the same kind of task,
 * so the controls for a field should not differ by which tab you reached it from.
 */
export function BlankTaskChips({
  control,
  values,
  options,
  projectId,
  boardId,
}: BlankTaskChipsProps) {
  const { t } = useTranslation('ai');
  const { dateFormat } = useDateFormat();

  return (
    <div className="flex flex-wrap gap-2.5 rounded-[10px] border p-3">
      {CHIPS.map(({ field, icon }) => (
        <TaskFieldChip
          key={field}
          field={field}
          testIdPrefix={TEST_ID_PREFIX}
          icon={icon}
          label={t(`quickCreate.fieldLabels.${field}`)}
          value={taskFieldChipValue(field, values[field], options, t, dateFormat)}
        >
          <TaskFieldEditor
            field={field}
            candidates={[]}
            control={control}
            options={options}
            projectId={projectId}
            boardId={boardId}
            projectStatuses={[]}
            selectedTags={[]}
            onTagsChange={() => undefined}
            testIdPrefix={TEST_ID_PREFIX}
          />
        </TaskFieldChip>
      ))}
    </div>
  );
}
